<template>
  <div class="min-h-screen flex">
    <!-- Sidebar -->
    <aside class="w-56 bg-dark-100 border-r border-secondary-800">
      <div class="h-16 flex items-center px-6 border-b border-secondary-800">
        <img src="/logo.svg" alt="Assertly Logo" class="h-8" />
      </div>
      <nav class="p-4 space-y-2">
        <router-link 
          v-for="item in navigation" 
          :key="item.path"
          :to="item.path"
          class="nav-link flex items-center space-x-3"
          :class="{ 'nav-link-active': $route.path.startsWith(item.path) }"
        >
          <component :is="item.icon" class="w-5 h-5" />
          <span>{{ item.name }}</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header class="h-16 bg-dark-100 border-b border-secondary-800 flex items-center justify-between px-6">
        <div class="flex items-center space-x-4">
          <h2 class="text-lg font-medium">{{ currentPageTitle }}</h2>
        </div>
        <div class="text-sm text-gray-400">
            {{ currentUser?.organization?.name || 'No Organization' }}
          </div>
        <div class="flex items-center space-x-4">
          <NotificationsPopover />
          <div class="relative">
            <button 
              @click="isUserMenuOpen = !isUserMenuOpen"
              class="flex items-center space-x-2 text-sm focus:outline-none"
            >
              <div class="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center overflow-hidden">
                <img 
                  v-if="currentUser?.avatar" 
                  :src="currentUser.avatar" 
                  class="w-full h-full object-cover"
                  :alt="`${currentUser.firstName} ${currentUser.lastName}`"
                />
                <span v-else class="font-medium text-white">
                  {{ currentUser ? `${currentUser.firstName?.[0]}${currentUser.lastName?.[0]}` : 'NA' }}
                </span>
              </div>
              <span>{{ userName }}</span>
              <ChevronDownIcon class="w-4 h-4 text-gray-400" />
            </button>
            <!-- User Menu Dropdown -->
            <div 
              v-if="isUserMenuOpen"
              class="absolute right-0 mt-2 w-48 bg-dark-100 border border-secondary-800 rounded-lg shadow-lg py-1"
            >
              <router-link 
                to="/profile" 
                class="block px-4 py-2 text-sm hover:bg-dark-200"
                @click="isUserMenuOpen = false"
              >
                Profile
              </router-link>
              <router-link 
                to="/settings" 
                class="block px-4 py-2 text-sm hover:bg-dark-200"
                @click="isUserMenuOpen = false"
              >
                Settings
              </router-link>
              <div class="border-t border-secondary-800 my-1"></div>
              <button 
                @click="signOut"
                class="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-dark-200"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 p-6 overflow-auto">
        <router-view></router-view>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  HomeIcon,
  ClipboardDocumentCheckIcon,
  PlayIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChartBarIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import NotificationsPopover from '@/components/notifications/NotificationsPopover.vue'

const route = useRoute()
const router = useRouter()
const isUserMenuOpen = ref(false)

const authStore = useAuthStore()
const { currentUser, userName } = authStore

const navigation = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { name: 'Test Cases', path: '/test-cases', icon: ClipboardDocumentCheckIcon },
  { name: 'Test Runs', path: '/test-execution', icon: PlayIcon },
  { name: 'Automated Tests', path: '/automation', icon: Cog6ToothIcon },
  { name: 'Reports', path: '/reports', icon: ChartBarIcon },
]

const currentPageTitle = computed(() => {
  const currentRoute = navigation.find(item => route.path.startsWith(item.path))
  return currentRoute ? currentRoute.name : ''
})

const signOut = () => {
  authStore.clearAuth()
  // Close the dropdown
  isUserMenuOpen.value = false
  // Redirect to login page
  router.push('/login')
}
</script>
